import React, { useState, useEffect } from 'react';
import fr from 'facet-rest';
import moment from 'moment';
import PropTypes from 'prop-types';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import Typography from '@material-ui/core/Typography';
import FileChips from 'Components/UI/FileChips';
import { withStyles } from '@material-ui/core/styles';
import relationships from 'Consts/Relationships';
import Button from 'Components/UI/Button';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import useCollection from 'Hooks/useCollection';
import featureEnabled from 'Helpers/Affordances';
import { Collapse } from '@mui/material';
import SettingsIcon from '@material-ui/icons/Settings';
import RecategorizeEmailDialog from 'Components/Modals/RecategorizeEmail';
import { getTimezoneOffsetInHours } from 'Helpers/DateTime';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    padding: theme.spacing(1),
    borderTop: '1px solid #ccc',
    flexWrap: 'wrap',
    '&:last-child': {
      borderBottom: '1px solid #ccc',
    },
  },
  header: {
    display: 'flex',
    width: '100%',
    cursor: 'pointer',
    alignItems: 'center',
  },
  subject: {
    flex: '4 0 0',
    display: 'flex',
    alignItems: 'center',
  },
  attachments: {
    display: 'flex',
    alignItems: 'center',
  },
  expandButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: theme.spacing(1),
  },
});

function PreviousEmail({ classes, agenda }) {
  const [expanded, setExpanded] = useState(null);
  const [isReassociateAgendaDialogVisible, setReassociateAgendaDialogVisibility] = useState(false);
  const {
    collection: attachmentsCollection,
    setCollection: setAttachmentsCollection,
  } = useCollection();

  const {
    collection: reassociatedAgendaCollection,
    setCollection: setReassociatedAgendaCollection,
  } = useCollection();

  const canReassociateAgenda = featureEnabled(agenda, fr.relationships.reassociateAgenda.name);

  const handleReassociateClick = (event) => {
    event.stopPropagation();
    setReassociateAgendaDialogVisibility(true);
  };

  useEffect(() => {
    if (agenda?.relationship(relationships.attachments)?.link('related')) {
      agenda
        .relationship(relationships.attachments)
        .link('related')
        .Get()
        .then(coll => setAttachmentsCollection(coll));
    }
  }, [agenda.id()]);

  useEffect(() => {
    if (agenda?.relationship(fr.relationships.reassociateAgenda.name)?.link('related')) {
      agenda
        .relationship(fr.relationships.reassociateAgenda.name)
        .link('related')
        .get({
          params: {
            timezoneOffset: getTimezoneOffsetInHours(),
          },
        })
        .then(coll => {
          setReassociatedAgendaCollection(coll);
        });
    }
  }, [agenda.id()]);

  return (
    <div className={classes.root}>
      {reassociatedAgendaCollection && (
        <RecategorizeEmailDialog
          isOpen={isReassociateAgendaDialogVisible}
          closeModal={() => setReassociateAgendaDialogVisibility(false)}
          agendaResource={reassociatedAgendaCollection.resources().head()}
        />)
      }
      <div
        className={classes.header}
        role="presentation"
        onClick={() => setExpanded(!expanded)}
      >
        <Typography color="primary" variant="body" className={classes.subject}>
          {`${agenda.attr('subject')} | ${moment(
            agenda.attr('sentTime'),
          ).format('M/D/YY, h:mmA')}`}
        </Typography>
        {attachmentsCollection
          && attachmentsCollection.resources().list().length > 0 && (
            <AttachFileIcon />)
        }
        {
          canReassociateAgenda && (
            <IconButton
              edge="end"
              className={classes.icon}
              aria-label="Reassociate Agenda"
              title="Reassociate Agenda"
              color="default"
              icon={SettingsIcon}
              onClick={handleReassociateClick}
            />)
        }
        <div className={classes.expandButton}>
          <Button
            size="small"
            color="primary"
            onClick={() => setExpanded(!expanded)}
            variant="icon"
            className={classes.iconButton}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
        </div>
      </div>
      <Collapse
        className={classes.description}
        classes={{
          wrapper: classes.collapseWrapper,
        }}
        in={expanded}
      >
        <div>
          <RichTextDisplay content={agenda.attr('content')} />
          {attachmentsCollection && (
            <FileChips
              fileCollection={attachmentsCollection}
              allowDelete={false}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
}

PreviousEmail.propTypes = {
  classes: PropTypes.object.isRequired,
  agenda: PropTypes.object.isRequired,
};

export default withStyles(styles)(PreviousEmail);
